import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ROUTE_TYPES } from "../../classes/constants/routeConstants";
import { useAppContext } from "../../classes/contexts/AppContext";
import { useRemoteRenderContext } from "../../classes/contexts/RemoteRenderContext";
import { useUser } from "../../classes/contexts/UserContext";
import IconsEnum from "../../classes/enums/IconsEnum";
import { MedicalImagesType } from "../../classes/enums/MedicalImagesEnum";
import { PLANNING_STATES } from "../../classes/enums/PlanningStateEnum";
import { SidebarSize } from "../../classes/enums/SideBarsEnum";
import { TargetTypeEnum } from "../../classes/enums/SppEnum";
import {
  ActNatures,
  SpineRigidityType,
  SurgerySidebarCategories,
} from "../../classes/enums/SurgicalActsEnum";
import { POPUP_CONTENT_TYPES, ToolShape } from "../../classes/enums/ToolsEnum";
import { vertebraUtils } from "../../classes/utils/AnnotationsUtils";
import { formatTwinCreationDate } from "../../classes/utils/DateUtils";
import { getRemoteMedicalViewState } from "../../classes/utils/RemoteMedicalViewStateUtils";
import IconButton from "../../components/iconButton/IconButton";
import ImageViewer from "../../components/imageViewer/ImageViewer";
import NeutralToggle from "../../components/neutralToggle/NeutralToggle";
import RemoteViewer from "../../components/remoteViewer/RemoteViewer";
import ViewLoader from "../../components/viewLoader/ViewLoader";
import useTranslation from "../../hooks/i18n/useTranslation";
import { usePatientInstancesByModality } from "../../hooks/queries/usePatientInstancesByModality";
import { useDataBundle } from "../../hooks/useDigitalTwinDataBundle";
import useMeasurementsActions from "../../hooks/useMeasurementsActions";
import usePlanSelection from "../../hooks/usePlanSelection";
import useRemoteViewActions from "../../hooks/useRemoteViewActions";
import { useRouteState } from "../../hooks/useRouteState";
import { useSppActions } from "../../hooks/useSppActions";
import useSurgicalActActions from "../../hooks/useSurgicalActActions";
import measurementToolManager from "../../services/MeasurementToolService";
import "../common/stepInteractionRegion/VisualizationArea.css";
import ToolBar from "../common/toolBar/ToolBar";
import ToolToggle from "../common/toolBar/ToolToggle";
import AlignToolsWidget from "./actsHistory/AlignToolsWidget";
import "./Align.css";
import Cage from "./alignParameterSelector/Cage";
import GlobalAlignment from "./alignParameterSelector/GlobalAlignment";
import Osteotomy from "./alignParameterSelector/Osteotomy";
import Rod from "./alignParameterSelector/Rod";
import SegmentalTtransformation from "./alignParameterSelector/SegmentalTtransformation";
import AlignSpineOverlayTools from "./alignTools/AlignSpineOverlayTools";
import useAlignDerivedData from "./hooks/useAlignDerivedData";
import useAlignMeasurements from "./hooks/useAlignMeasurements";
import useAlignMeasurementsSync from "./hooks/useAlignMeasurementsSync";
import useAlignSurgicalActsState from "./hooks/useAlignSurgicalActsState";
import useAlignViewManagement from "./hooks/useAlignViewManagement";
import useVertebraRangeSelection from "./hooks/useVertebraRangeSelection";
import PlanTab from "./planTab/PlanTab";
import RodsSelector from "./rodsSelector/RodsSelector";
import AlignSpp from "./spp/AlignSppWidget";

// Normalize view name to display settings key
const getDisplaySettingsViewType = viewName => {
  if (!viewName) return null;
  const lower = viewName.toLowerCase();
  if (lower.includes("lateral") || lower.includes("sagittal")) return "lateral";
  if (lower.includes("front") || lower.includes("coronal")) return "frontal";
  if (lower.includes("axial")) return "axial";
  return null;
};

const Align = () => {
  const { t } = useTranslation();
  const {
    dataBundle,
    constructedImageData,
    instances,
    selectedTwin,
    measurementRefresh,
    validationRefresh,
    surgicalActsRefresh,
    plansRefresh,
    isSagittalDxValidated,
    loadingState,
    currentPlan,
    currentPlanId,
    // Comparison mode data
    comparedPlan,
    comparedPlanId,
    comparedSurgicalActs,
    comparedGlobalAlignmentAct,
  } = useDataBundle();
  const { dateSettings } = useUser();

  const isComparisonMode = comparedPlanId != null;

  const {
    client: renderClient,
    digitalTwinSurgeryCache,
    surgerySessionVersions,
    isConnected: isRemoteRenderConnected,
    isLoading: isRemoteRenderLoading,
  } = useRemoteRenderContext();

  const { instancesByModality } = usePatientInstancesByModality();
  const { showOnlyRightSidebar, hideSidebars } = useAppContext();
  const { navigateToValidation, params } = useRouteState();

  // UI State
  const [activeSidebar, setActiveSidebar] = useState(
    SurgerySidebarCategories.spp
  );
  const [selectedTargetType, setSelectedTargetType] = useState(
    TargetTypeEnum.GAP
  );
  const [selectedModality, setSelectedModality] = useState(null);
  const [selectedSurgeryTool, setSelectedSurgeryTool] = useState(null);
  const [isRodToolActive, setIsRodToolActive] = useState(false);
  const [isOverlayToolsActive, setIsOverlayToolsActive] = useState(true);
  const [showSupportView, setShowSupportView] = useState(false);
  const [overlayToolsState, setOverlayToolsState] = useState({});
  const [comparedOverlayToolsState, setComparedOverlayToolsState] = useState(
    {}
  );

  const [, setIsLoadingRodTool] = useState(false);
  const [canApplyReciprocalChange, setCanApplyReciprocalChange] =
    useState(false);
  const [selectedSpineType, setSelectedSpineType] = useState(
    SpineRigidityType.RIGID
  );

  // Surgery loading state
  const twinId = selectedTwin?.[0]?._id || "";
  const [isLoading, setIsLoading] = useState(false);
  const [areViewsReady, setAreViewsReady] = useState(false);
  const controlsDisabled = isLoading || !areViewsReady;
  const alignSessionVersion =
    surgerySessionVersions?.[PLANNING_STATES.ALIGN] ?? 0;
  const [editingAct, setEditingAct] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showParameterSelectorViewer, setShowParameterSelectorViewer] =
    useState(false);

  const DEFAULT_ROD_PAIR_ID = "rod-1";
  const createEmptyRod = useCallback(
    (id = DEFAULT_ROD_PAIR_ID) => ({
      id,
      rodPairId: `rod-pair-${id.replace("rod-", "")}`,
      range: [],
      selectedLeftScrews: [],
      selectedRightScrews: [],
      pendingFirstVertebra: null,
      rodDiameter: 6,
      overhangLength: 10,
      rodType: "Patient Specific",
      rodManufacturer: "MDsim",
      rodProduct: "",
      rodMaterial: "Titanium",
      screwManufacturer: "",
      screwProduct: "",
      screwDiameter: 6.5,
      screwLength: 45,
      screwMaterial: "Titanium",
    }),
    []
  );
  const [rods, setRods] = useState([createEmptyRod()]);
  const [screwSpecs, setScrewSpecs] = useState({});
  const nextRodIndexRef = useRef(2);

  const activeRodId = rods[0]?.id || DEFAULT_ROD_PAIR_ID;

  const {
    originalMeasurements,
    currentMeasurements,
    targetMeasurements,
    comparedCurrentMeasurements,
    refetchMeasurements,
    registerSyncedMeasurements,
  } = useAlignMeasurements({
    instances,
    currentPlan,
    comparedPlan,
  });

  const handleMeasurementsSynced = useCallback(
    syncResult => {
      if (!syncResult) {
        return;
      }

      if (syncResult.planId && syncResult.planId !== currentPlanId) {
        return;
      }

      registerSyncedMeasurements(syncResult);

      const hasNewPlanMeasurements =
        syncResult?.current?.createdIds?.length ||
        syncResult?.target?.createdIds?.length;
      const hasNewInstanceMeasurements =
        syncResult?.original?.createdIds?.length;

      if (hasNewPlanMeasurements) {
        plansRefresh?.();
      }

      if (hasNewInstanceMeasurements) {
        validationRefresh?.();
      }

      refetchMeasurements?.();
    },
    [
      registerSyncedMeasurements,
      plansRefresh,
      validationRefresh,
      refetchMeasurements,
      currentPlanId,
    ]
  );

  const { syncMeasurementsWithInstances, syncMeasurementsWithPlan } =
    useMeasurementsActions();

  const { resetView } = useRemoteViewActions(currentPlanId);

  useAlignMeasurementsSync({
    instances,
    currentPlan,
    currentPlanId,
    comparedPlan,
    comparedPlanId,
    isComparisonMode,
    syncMeasurementsWithInstances,
    syncMeasurementsWithPlan,
    onMeasurementsSynced: handleMeasurementsSynced,
  });

  const {
    addCustomMeasurement,
    onSppSelected,
    onSppSelectedCustom,
    activeParameters,
    removeCustomParameter,
    syncComparedPlanSppVisibility,
  } = useSppActions(
    areViewsReady,
    measurementRefresh,
    currentPlanId,
    comparedPlanId
  );

  const {
    performDecompensation,
    getInitialGlobalAlignment,
    updateGlobalAlignment,
    startSurgery,
    syncAllSurgicalActs,
    deleteSurgicalAct,
    showRodTool,
    removeRodPreview,
  } = useSurgicalActActions(currentPlanId);

  const {
    surgicalActs,
    globalAlignmentAct,
    preOpGlobalAlignment,
    saasGlobalAlignment,
    gapGlobalAlignment,
    getGlobalAlignmentInfo,
    addSurgicalActs,
    updateSurgicalActs,
    deleteSurgicalActEntry,
    bulkDeleteSurgicalActs,
    deleteGlobalAlignmentEntry,
    manualGlobalAlignment,
  } = useAlignSurgicalActsState({
    dataBundle,
    currentPlan,
    currentPlanId,
    areViewsReady,
    setIsLoading,
    startSurgery,
    syncAllSurgicalActs,
    surgicalActsRefresh,
    plansRefresh,
    deleteSurgicalAct,
    performDecompensation,
    getInitialGlobalAlignment,
  });

  const normalizeVertebraSelection = useCallback(selection => {
    if (!selection) return [];
    const list = Array.isArray(selection) ? selection : [selection];
    return vertebraUtils.expandSacrumSelection(list);
  }, []);

  const handleAddRod = useCallback(() => {
    const nextId = `rod-${nextRodIndexRef.current}`;
    nextRodIndexRef.current += 1;
    setRods(prev => {
      const nextState = [...prev, createEmptyRod(nextId)];
      return nextState;
    });
    return nextId;
  }, [createEmptyRod]);

  const handleUpdateRod = useCallback((rodId, updater) => {
    setRods(prev =>
      prev.map(rod =>
        rod.id === rodId
          ? {
              ...rod,
              ...(typeof updater === "function" ? updater(rod) : updater),
            }
          : rod
      )
    );
  }, []);

  const handleRemoveRod = useCallback(rodId => {
    setRods(prev => {
      if (prev.length <= 1) {
        return prev;
      }
      const rodToRemove = prev.find(rod => rod.id === rodId);
      const nextState = prev.filter(rod => rod.id !== rodId);
      if (rodToRemove) {
        const removalSet = new Set([
          ...(rodToRemove.selectedLeftScrews || []),
          ...(rodToRemove.selectedRightScrews || []),
        ]);
        setScrewSpecs(prevSpecs => {
          const nextSpecs = { ...prevSpecs };
          removalSet.forEach(id => delete nextSpecs[id]);
          return nextSpecs;
        });
      }
      return nextState;
    });
  }, []);

  const specsAreEqual = useCallback((a, b) => {
    if (a === b) return true;
    const aKeys = Object.keys(a || {});
    const bKeys = Object.keys(b || {});
    if (aKeys.length !== bKeys.length) return false;
    for (const key of aKeys) {
      const aVal = a[key] || {};
      const bVal = b[key] || {};
      if (aVal.diameter !== bVal.diameter || aVal.length !== bVal.length) {
        return false;
      }
    }
    return true;
  }, []);

  const updateScrewSpecs = useCallback(
    updater => {
      setScrewSpecs(prev => {
        const next =
          typeof updater === "function" ? updater(prev) : updater || {};
        if (specsAreEqual(prev, next)) {
          return prev;
        }
        return next;
      });
    },
    [specsAreEqual]
  );

  const resetRodPairs = useCallback(() => {
    const resetState = [createEmptyRod()];
    setRods(resetState);
    setScrewSpecs({});
  }, [createEmptyRod]);

  const getStoredSpineType = useCallback(() => {
    const reciprocalChangeAct = Object.values(surgicalActs).find(
      act => act.actNature === ActNatures.ReciprocalChange
    );
    return reciprocalChangeAct?.spineRigidityType || SpineRigidityType.RIGID;
  }, [surgicalActs]);

  const alignAnatomyProfile = useMemo(() => {
    const sourceInstance =
      instances?.sagittal_dx_instance_id ||
      instances?.coronal_dx_instance_id ||
      instances?.ct_instance_id ||
      Object.values(instances || {})[0] ||
      null;

    return {
      hasL6: sourceInstance?.has_s1_lumbarized === true,
      hasL5Sacralized: sourceInstance?.has_l5_sacralized === true,
      hasT13: sourceInstance?.has_t_13 === true,
    };
  }, [instances]);

  const vertebraRangeSelection = useVertebraRangeSelection(
    rods,
    handleUpdateRod,
    surgicalActs,
    alignAnatomyProfile
  );

  useEffect(() => {
    setSelectedSpineType(getStoredSpineType());
  }, [getStoredSpineType]);

  useEffect(() => {
    if (selectedSurgeryTool === POPUP_CONTENT_TYPES.FUSION) {
      setSelectedSpineType(getStoredSpineType());
    }
  }, [getStoredSpineType, selectedSurgeryTool]);

  // region INITIALIZATION

  const {
    groupedThumbnailsStandard,
    allInstances,
    selectedTwinName,
    digitalTwinId,
    initialKeyParameters,
    selectedPlanId,
  } = useAlignDerivedData({
    instances,
    instancesByModality,
    selectedTwin,
  });

  const {
    selectedPlanId: persistedSelectedPlanId,
    isSavingSelection,
    setPlanAsSelected,
  } = usePlanSelection({
    digitalTwinId,
    initialSelectedPlanId: selectedPlanId,
  });

  const getPlanDisplayName = useCallback(
    (plan, planId) => {
      if (plan?.name) {
        return plan.name;
      }

      const plans = dataBundle.allPlans || [];
      const targetId = plan?._id || planId;
      const planIndex = targetId
        ? plans.findIndex(p => p._id === targetId)
        : -1;

      if (planIndex >= 0) {
        return `Plan ${planIndex + 1}`;
      }

      return "Plan";
    },
    [dataBundle.allPlans]
  );

  const currentPlanLabel = useMemo(
    () => getPlanDisplayName(currentPlan, currentPlanId),
    [getPlanDisplayName, currentPlan, currentPlanId]
  );

  const comparedPlanLabel = useMemo(
    () => getPlanDisplayName(comparedPlan, comparedPlanId),
    [getPlanDisplayName, comparedPlan, comparedPlanId]
  );

  const comparedGlobalAlignment = useMemo(() => {
    if (!comparedGlobalAlignmentAct) {
      return null;
    }

    if (Array.isArray(comparedGlobalAlignmentAct)) {
      return comparedGlobalAlignmentAct[0] || null;
    }

    return comparedGlobalAlignmentAct;
  }, [comparedGlobalAlignmentAct]);

  const handleSelectCurrentPlan = useCallback(
    async newState => {
      if (!digitalTwinId) {
        return;
      }

      const targetPlanId = newState ? currentPlanId : null;
      if (newState && !currentPlanId) {
        return;
      }

      try {
        await setPlanAsSelected(targetPlanId);
      } catch (error) {
        console.error("[Align] Failed to set selected plan", error);
      }
    },
    [currentPlanId, digitalTwinId, setPlanAsSelected]
  );

  const isCurrentPlanSelected =
    !!currentPlanId && currentPlanId === persistedSelectedPlanId;

  // region CALLBACKS
  const handleSppToggle = useCallback(() => {
    setActiveSidebar(prev =>
      prev === SurgerySidebarCategories.spp
        ? null
        : SurgerySidebarCategories.spp
    );
  }, []);

  const handleHistoryToggle = useCallback(() => {
    setActiveSidebar(prev =>
      prev === SurgerySidebarCategories.history
        ? null
        : SurgerySidebarCategories.history
    );
  }, []);

  const initializeRodsFromSurgicalActs = useCallback(() => {
    // Extract rod acts from surgicalActs
    const rodActs = Object.values(surgicalActs).filter(
      act => act.actNature === ActNatures.Rod
    );

    if (rodActs.length === 0) {
      resetRodPairs();
      return;
    }

    const rodPairs = new Map();

    rodActs.forEach(act => {
      // Use rodPairId if available, otherwise fall back to range-based grouping
      const pairKey = act.rodPairId || (act.rodVertebraeSubset || []).join("-");
      if (!rodPairs.has(pairKey)) {
        rodPairs.set(pairKey, {
          left: null,
          right: null,
          pairId: act.rodPairId,
        });
      }
      const pair = rodPairs.get(pairKey);
      if (act.side === "left") {
        pair.left = act;
      } else if (act.side === "right") {
        pair.right = act;
      }
    });

    // Convert pairs to rod state objects
    const newRods = [];
    let rodIndex = 1;

    rodPairs.forEach((pair, pairKey) => {
      const refAct = pair.left || pair.right;
      if (!refAct) return;

      // Compute combined range from both sides
      const leftRange = pair.left
        ? normalizeVertebraSelection(pair.left.rodVertebraeSubset || [])
        : [];
      const rightRange = pair.right
        ? normalizeVertebraSelection(pair.right.rodVertebraeSubset || [])
        : [];
      // Combine and sort by vertebra order
      const combinedRange = [...new Set([...leftRange, ...rightRange])];
      const normalizedRange =
        vertebraUtils.expandSacrumSelection(combinedRange);

      const rodState = {
        id: `rod-${rodIndex}`,
        rodPairId: pair.pairId || `rod-pair-${rodIndex}`, // Preserve or generate pairId
        range: normalizedRange,
        selectedLeftScrews: leftRange,
        selectedRightScrews: rightRange,
        rodDiameter: refAct.rodDiameter || 6.0,
        overhangLength: refAct.overhangLength || refAct.rodExtraLength || 10,
        rodType: refAct.rodType || "Patient Specific",
        rodManufacturer: refAct.rodManufacturer || "MDsim",
        rodProduct: refAct.rodProduct || "",
        rodMaterial: refAct.rodMaterial || "Titanium",
        screwManufacturer: refAct.screwManufacturer || "",
        screwProduct: refAct.screwProduct || "",
        screwDiameter: refAct.screwDiameter || 6.5,
        screwLength: refAct.screwLength || 45,
        screwMaterial: refAct.screwMaterial || "Titanium",
        pendingFirstVertebra: null,
      };

      newRods.push(rodState);
      rodIndex++;
    });

    if (newRods.length > 0) {
      nextRodIndexRef.current = rodIndex;
      setRods(newRods);
    } else {
      resetRodPairs();
    }
  }, [surgicalActs, normalizeVertebraSelection, resetRodPairs]);

  const handleSurgeryToolSelect = useCallback(
    async toolType => {
      if (selectedSurgeryTool === toolType) {
        setSelectedSurgeryTool(null);
      } else {
        setSelectedSurgeryTool(toolType);

        if (toolType === POPUP_CONTENT_TYPES.FUSION) {
          setSelectedSpineType(getStoredSpineType());
          initializeRodsFromSurgicalActs();
          setIsRodToolActive(true);
          setActiveSidebar(SurgerySidebarCategories.history);
        } else if (toolType === POPUP_CONTENT_TYPES.GLOBAL_ALIGNMENT) {
          setIsOverlayToolsActive(false);
          await getGlobalAlignmentInfo();
        } else {
          setIsRodToolActive(false);
        }
      }
    },
    [
      selectedSurgeryTool,
      initializeRodsFromSurgicalActs,
      getGlobalAlignmentInfo,
      selectedSpineType,
      getStoredSpineType,
    ]
  );

  const handleEditSurgicalAct = useCallback(
    async actToEdit => {
      setEditingAct(actToEdit);
      setIsEditMode(true);

      let toolType;
      switch (actToEdit.actNature) {
        case ActNatures.Osteotomy:
          toolType = POPUP_CONTENT_TYPES.OSTEOTOMY;
          break;
        case ActNatures.Cage:
          toolType = POPUP_CONTENT_TYPES.CAGES;
          break;
        case ActNatures.Rod:
          toolType = POPUP_CONTENT_TYPES.FUSION;
          if (actToEdit.rodVertebraeSubset) {
            const normalizedSubset = normalizeVertebraSelection(
              actToEdit.rodVertebraeSubset
            );
            handleUpdateRod(activeRodId, prev => {
              const nextState = { ...prev, range: normalizedSubset };
              if (actToEdit.side === "left") {
                nextState.selectedLeftScrews = normalizedSubset;
                nextState.selectedRightScrews = [];
              } else if (actToEdit.side === "right") {
                nextState.selectedLeftScrews = [];
                nextState.selectedRightScrews = normalizedSubset;
              } else {
                nextState.selectedLeftScrews = normalizedSubset;
                nextState.selectedRightScrews = normalizedSubset;
              }
              if (actToEdit.rodDiameter) {
                nextState.rodDiameter = actToEdit.rodDiameter;
              }
              if (actToEdit.overhangLength || actToEdit.rodExtraLength) {
                nextState.overhangLength =
                  actToEdit.overhangLength || actToEdit.rodExtraLength;
              }
              if (actToEdit.rodType) {
                nextState.rodType = actToEdit.rodType;
              }
              if (actToEdit.rodManufacturer) {
                nextState.rodManufacturer = actToEdit.rodManufacturer;
              }
              if (actToEdit.rodProduct) {
                nextState.rodProduct = actToEdit.rodProduct;
              }
              if (actToEdit.rodMaterial) {
                nextState.rodMaterial = actToEdit.rodMaterial;
              }
              if (actToEdit.screwManufacturer) {
                nextState.screwManufacturer = actToEdit.screwManufacturer;
              }
              if (actToEdit.screwProduct) {
                nextState.screwProduct = actToEdit.screwProduct;
              }
              if (actToEdit.screwDiameter) {
                nextState.screwDiameter = actToEdit.screwDiameter;
              }
              if (actToEdit.screwLength) {
                nextState.screwLength = actToEdit.screwLength;
              }
              if (actToEdit.screwMaterial) {
                nextState.screwMaterial = actToEdit.screwMaterial;
              }
              return nextState;
            });
          }
          break;
        case ActNatures.SegmentalTransformation:
          toolType = POPUP_CONTENT_TYPES.SEGMENTAL_TRANSFORMATION;
          break;
        case ActNatures.GlobalAlignment:
          toolType = POPUP_CONTENT_TYPES.GLOBAL_ALIGNMENT;
          break;
        default:
          return;
      }

      setSelectedSurgeryTool(toolType);
    },
    [activeRodId, normalizeVertebraSelection, handleUpdateRod]
  );

  const handleEditAllRods = useCallback(
    rodActs => {
      if (!rodActs || rodActs.length === 0) return;
      const rodPairs = new Map();

      rodActs.forEach(act => {
        const pairKey =
          act.rodPairId || (act.rodVertebraeSubset || []).join("-");
        if (!rodPairs.has(pairKey)) {
          rodPairs.set(pairKey, {
            left: null,
            right: null,
            pairId: act.rodPairId,
          });
        }
        const pair = rodPairs.get(pairKey);
        if (act.side === "left") {
          pair.left = act;
        } else if (act.side === "right") {
          pair.right = act;
        }
      });

      const newRods = [];
      let rodIndex = 1;

      rodPairs.forEach((pair, pairKey) => {
        const refAct = pair.left || pair.right;
        if (!refAct) return;

        const leftRange = pair.left
          ? normalizeVertebraSelection(pair.left.rodVertebraeSubset || [])
          : [];
        const rightRange = pair.right
          ? normalizeVertebraSelection(pair.right.rodVertebraeSubset || [])
          : [];

        const combinedRange = [...new Set([...leftRange, ...rightRange])];
        const normalizedRange =
          vertebraUtils.expandSacrumSelection(combinedRange);

        const rodState = {
          id: `rod-${rodIndex}`,
          rodPairId: pair.pairId || `rod-pair-${rodIndex}`,
          range: normalizedRange,
          selectedLeftScrews: leftRange,
          selectedRightScrews: rightRange,
          rodDiameter: refAct.rodDiameter || 6.0,
          overhangLength: refAct.overhangLength || refAct.rodExtraLength || 10,
          rodType: refAct.rodType || "Patient Specific",
          rodManufacturer: refAct.rodManufacturer || "MDsim",
          rodProduct: refAct.rodProduct || "",
          rodMaterial: refAct.rodMaterial || "Titanium",
          screwManufacturer: refAct.screwManufacturer || "",
          screwProduct: refAct.screwProduct || "",
          screwDiameter: refAct.screwDiameter || 6.5,
          screwLength: refAct.screwLength || 45,
          screwMaterial: refAct.screwMaterial || "Titanium",
          pendingFirstVertebra: null,
        };

        newRods.push(rodState);
        rodIndex++;
      });

      if (newRods.length > 0) {
        nextRodIndexRef.current = rodIndex;
        setRods(newRods);
        setIsEditMode(true);
        setSelectedSurgeryTool(POPUP_CONTENT_TYPES.FUSION);
      }
    },
    [normalizeVertebraSelection]
  );

  const handleEditGlobalAlignment = useCallback(
    async globalAlignmentToEdit => {
      setEditingAct(globalAlignmentToEdit);
      setIsEditMode(true);
      setSelectedSurgeryTool(POPUP_CONTENT_TYPES.GLOBAL_ALIGNMENT);
      await getGlobalAlignmentInfo();
    },
    [getGlobalAlignmentInfo]
  );

  const handleCloseToolWidget = useCallback(async () => {
    setEditingAct(null);
    setIsEditMode(false);
    setShowParameterSelectorViewer(false);
    setSelectedSurgeryTool(null);
    setIsOverlayToolsActive(true);
  }, []);

  const handleRequiredFieldsChange = useCallback(
    (toolType, hasRequiredFields) => {
      setShowParameterSelectorViewer(hasRequiredFields);
    },
    []
  );

  const handleTargetTypeChange = useCallback(newTargetType => {
    setSelectedTargetType(newTargetType);
  }, []);

  const handleAddSurgicalActs = useCallback(
    async surgicalActsToAdd => {
      await addSurgicalActs(surgicalActsToAdd, {
        onCloseToolWidget: () => handleCloseToolWidget(),
        onFinally: sidebar => setActiveSidebar(sidebar),
      });
    },
    [addSurgicalActs, handleCloseToolWidget, setActiveSidebar]
  );

  const handleUpdateSurgicalActs = useCallback(
    async surgicalActsToUpdate => {
      await updateSurgicalActs(surgicalActsToUpdate, {
        onCloseToolWidget: () => handleCloseToolWidget(),
        onFinally: sidebar => setActiveSidebar(sidebar),
        canApplyReciprocalChange,
      });
    },
    [
      updateSurgicalActs,
      handleCloseToolWidget,
      setActiveSidebar,
      canApplyReciprocalChange,
    ]
  );

  const handleDeleteSurgicalAct = useCallback(
    async surgicalActToDelete => {
      await deleteSurgicalActEntry(surgicalActToDelete);
    },
    [deleteSurgicalActEntry]
  );

  const handleBulkDeleteSurgicalActs = useCallback(
    async surgicalActsToDelete => {
      await bulkDeleteSurgicalActs(surgicalActsToDelete);
    },
    [bulkDeleteSurgicalActs]
  );

  const handleDeleteGlobalAlignment = useCallback(
    async globalAlignmentToDelete => {
      await deleteGlobalAlignmentEntry(globalAlignmentToDelete);
    },
    [deleteGlobalAlignmentEntry]
  );

  const handleManualGlobalAlignment = useCallback(
    async globalAlignmentActNew => {
      await manualGlobalAlignment(globalAlignmentActNew, {
        onCloseToolWidget: handleCloseToolWidget,
        isOverlayToolsActive,
      });
    },
    [handleCloseToolWidget, isOverlayToolsActive, manualGlobalAlignment]
  );

  const handleUpdateGlobalAlignment = useCallback(
    //start loading effect and just call the update function
    async globalAlignmentToUpdate => {
      setIsLoading(true);
      try {
        await updateGlobalAlignment(globalAlignmentToUpdate);
      } finally {
        setIsLoading(false);
      }
    },
    [updateGlobalAlignment]
  );

  // region SETUP

  const setGlobalAlignmentSelectionFirstTime = useCallback(async () => {
    if (!currentPlan?._id) {
      return;
    }

    setActiveSidebar(null);
    await handleSurgeryToolSelect(POPUP_CONTENT_TYPES.GLOBAL_ALIGNMENT);
  }, [currentPlan?._id, handleSurgeryToolSelect]);

  // region EFFECTS

  // region VALIDATION CHECK
  // Align requires sagittal DX specifically to be validated
  useEffect(() => {
    if (
      instances &&
      Object.keys(instances).length > 0 &&
      dataBundle.annotations &&
      Object.keys(dataBundle.annotations).length > 0
    ) {
      if (!isSagittalDxValidated) {
        navigateToValidation(params.patientId, params.twinId, "DX");
        return;
      }
    }
  }, [
    instances,
    dataBundle.annotations,
    isSagittalDxValidated,
    navigateToValidation,
    params.patientId,
    params.twinId,
  ]);

  // region ROD
  // Show rod tool when we have valid vertebrae range and diameter
  useEffect(() => {
    if (selectedSurgeryTool !== POPUP_CONTENT_TYPES.FUSION) {
      return;
    }

    let timeoutId = null;
    const rodPayload = rods
      .map(pair => {
        const left = (pair.selectedLeftScrews || []).filter(
          v => v && v !== "-"
        );
        const right = (pair.selectedRightScrews || []).filter(
          v => v && v !== "-"
        );
        if (
          (left.length > 1 || right.length > 1) &&
          pair.rodDiameter &&
          pair.overhangLength
        ) {
          return {
            key: pair.id,
            left,
            right,
            diameter: pair.rodDiameter,
            overhangLength: pair.overhangLength,
          };
        }
        return null;
      })
      .filter(Boolean);
    const hasRenderablePair = rodPayload.length > 0;

    if (hasRenderablePair) {
      timeoutId = setTimeout(async () => {
        setIsLoadingRodTool(true);
        try {
          try {
            await removeRodPreview();
          } catch (error) {
            console.warn("[Align][Rods] hide before render failed", error);
          }
          const spineTypeParam = canApplyReciprocalChange
            ? selectedSpineType
            : null;
          const reciprocalChangeEnabled = await showRodTool(
            rodPayload,
            spineTypeParam
          );
          setCanApplyReciprocalChange(reciprocalChangeEnabled || false);

          setIsRodToolActive(true);
        } catch (error) {
          console.error("[Align][Rods] error while rendering rods", error);
        } finally {
          setIsLoadingRodTool(false);
        }
      }, 100);
    } else {
      removeRodPreview().catch(() => {});
      if (isRodToolActive) {
        setIsRodToolActive(false);
        setIsLoadingRodTool(false);
      }
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [
    selectedSurgeryTool,
    removeRodPreview,
    rods,
    showRodTool,
    isRodToolActive,
    canApplyReciprocalChange,
    selectedSpineType,
  ]);

  const { currentModalityViews, comparedModalityViews, areComparedViewsReady } =
    useAlignViewManagement({
      selectedTwin,
      selectedModality,
      groupedThumbnailsStandard,
      alignSessionVersion,
      currentPlanId,
      digitalTwinSurgeryCache,
      setAreViewsReady,
      areViewsReady,
      twinId,
      renderClient,
      constructedImageData,
      allInstances,
      instances,
      loadingState,
      dataBundle,
      startSurgery,
      performDecompensation,
      setIsLoading,
      setGlobalAlignmentSelectionFirstTime,
      // Comparison mode params
      comparedPlanId,
      comparedSurgicalActs,
      comparedGlobalAlignmentAct,
    });
  const isComparedPlanLoading = isComparisonMode && !areComparedViewsReady;

  const lastSyncedComparedPlanIdRef = useRef(null);
  const isFirstToggleRender = useRef(true);
  useEffect(() => {
    // Skip the first render to avoid resetting on mount
    if (isFirstToggleRender.current) {
      isFirstToggleRender.current = false;
      return;
    }

    if (
      !areViewsReady ||
      !currentModalityViews ||
      currentModalityViews.length === 0
    ) {
      return;
    }

    const targetView = showSupportView
      ? currentModalityViews.find(
          v => v.viewName === MedicalImagesType.LATERAL_SUPPORT_SURGERY
        )
      : currentModalityViews[0];

    if (targetView?.viewData?.viewId) {
      const timeoutId = setTimeout(async () => {
        await resetView(targetView.viewData.viewId);
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [showSupportView, areViewsReady, currentModalityViews, resetView]);

  useEffect(() => {
    if (!isComparisonMode || !comparedPlanId) {
      lastSyncedComparedPlanIdRef.current = null;
      return;
    }

    if (!areComparedViewsReady) {
      return;
    }

    const syncComparedPlanVisibility = async () => {
      try {
        await syncComparedPlanSppVisibility();
        lastSyncedComparedPlanIdRef.current = comparedPlanId;
      } catch (error) {
        console.error(
          "[Align] Failed to sync compared plan SPP visibility",
          error
        );
      }
    };

    if (lastSyncedComparedPlanIdRef.current !== comparedPlanId) {
      syncComparedPlanVisibility();
    }
  }, [
    isComparisonMode,
    comparedPlanId,
    areComparedViewsReady,
    syncComparedPlanSppVisibility,
  ]);

  useEffect(() => {
    lastSyncedComparedPlanIdRef.current = null;
  }, [currentPlanId]);

  // region UTILS

  const has3dInstances = useMemo(() => {
    return (
      instancesByModality?.CT_Volume?.length > 0 ||
      instancesByModality?.MR_Volume?.length > 0
    );
  }, [instancesByModality]);

  // region RENDERING

  const renderSurgeryWidget = useCallback(() => {
    if (!selectedSurgeryTool) return null;

    const supportView = currentModalityViews.find(
      view => view.viewName === MedicalImagesType.LATERAL_SUPPORT_SURGERY
    );

    let widgetTitle = selectedSurgeryTool;
    let widgetComponent = null;

    switch (selectedSurgeryTool) {
      case POPUP_CONTENT_TYPES.OSTEOTOMY:
        widgetTitle = isEditMode ? "Edit Osteotomy" : "Osteotomy";
        widgetComponent = (
          <Osteotomy
            isEditMode={isEditMode}
            existingAct={editingAct}
            supportViewData={supportView?.viewData}
            hasL6={alignAnatomyProfile.hasL6}
            hasT13={alignAnatomyProfile.hasT13}
            onConfirm={handleAddSurgicalActs}
            onRequiredFieldsChange={handleRequiredFieldsChange}
          />
        );
        break;

      case POPUP_CONTENT_TYPES.SEGMENTAL_TRANSFORMATION:
        widgetTitle = isEditMode
          ? "Edit Move/ Rotate vertebrae"
          : "Move/ Rotate vertebrae";
        widgetComponent = (
          <SegmentalTtransformation
            isEditMode={isEditMode}
            existingAct={editingAct}
            supportViewData={supportView?.viewData}
            hasL6={alignAnatomyProfile.hasL6}
            hasT13={alignAnatomyProfile.hasT13}
            onConfirm={handleAddSurgicalActs}
            onRequiredFieldsChange={handleRequiredFieldsChange}
          />
        );
        break;

      case POPUP_CONTENT_TYPES.CAGES:
        widgetTitle = isEditMode ? "Edit Cage" : "Cages";
        widgetComponent = (
          <Cage
            isEditMode={isEditMode}
            existingAct={editingAct}
            supportViewData={supportView?.viewData}
            hasL6={alignAnatomyProfile.hasL6}
            hasT13={alignAnatomyProfile.hasT13}
            onConfirm={handleAddSurgicalActs}
            onRequiredFieldsChange={handleRequiredFieldsChange}
          />
        );
        break;

      case POPUP_CONTENT_TYPES.GLOBAL_ALIGNMENT:
        widgetTitle = isEditMode ? "Edit Decompensation" : "Decompensation";
        widgetComponent = (
          <GlobalAlignment
            isEditMode={isEditMode}
            onConfirm={handleManualGlobalAlignment}
            onUpdate={handleUpdateGlobalAlignment}
            existingGlobalAlignment={editingAct || globalAlignmentAct}
            preOpGlobalAlignment={preOpGlobalAlignment}
            saasGlobalAlignment={saasGlobalAlignment}
            gapGlobalAlignment={gapGlobalAlignment}
            measurementConfigs={dataBundle.measurementConfigsFrontEnd}
          />
        );
        break;

      case POPUP_CONTENT_TYPES.FUSION:
        widgetTitle = isEditMode ? "Edit Spine Fusion" : "Spine Fusion";
        widgetComponent = (
          <Rod
            isEditMode={isEditMode}
            existingAct={editingAct}
            rods={rods}
            onAddRod={handleAddRod}
            onUpdateRod={handleUpdateRod}
            onRemoveRod={handleRemoveRod}
            screwSpecs={screwSpecs}
            onConfirm={handleUpdateSurgicalActs}
            surgicalActs={surgicalActs}
            vertebraRangeSelection={vertebraRangeSelection}
            canApplyReciprocalChange={canApplyReciprocalChange}
            selectedSpineType={selectedSpineType}
            onSpineTypeChange={setSelectedSpineType}
          />
        );
        break;

      default:
        return null;
    }

    return {
      title: widgetTitle,
      component: widgetComponent,
    };
  }, [
    selectedSurgeryTool,
    currentModalityViews,
    isEditMode,
    editingAct,
    alignAnatomyProfile.hasL6,
    alignAnatomyProfile.hasT13,
    handleAddSurgicalActs,
    handleRequiredFieldsChange,
    handleManualGlobalAlignment,
    handleUpdateGlobalAlignment,
    globalAlignmentAct,
    preOpGlobalAlignment,
    saasGlobalAlignment,
    gapGlobalAlignment,
    dataBundle.measurementConfigsFrontEnd,
    rods,
    handleAddRod,
    handleUpdateRod,
    handleRemoveRod,
    screwSpecs,
    handleUpdateSurgicalActs,
    surgicalActs,
    vertebraRangeSelection,
    canApplyReciprocalChange,
    selectedSpineType,
  ]);

  const renderAlignView = () => {
    if (!selectedModality) {
      return <div className="no-view-message"></div>;
    }

    const views = currentModalityViews;
    const remoteMedicalViewState = getRemoteMedicalViewState({
      modality: selectedModality,
      views,
      isLocalLoading: isLoading || !areViewsReady,
      isRemoteRenderConnected,
      isRemoteRenderLoading,
    });
    const isAlignLoading = remoteMedicalViewState.isLoading;
    const remoteMedicalLoadingMessage = remoteMedicalViewState.isConnecting
      ? t("474")
      : t("475");

    if (views.length === 0 && !isAlignLoading) {
      return (
        <div className="no-view-message">
          No {selectedModality} alignment views available
        </div>
      );
    }

    const view = views[0];

    const getCurrentNeededView = () => {
      const view = views[0];
      const supportView = views.find(
        v => v.viewName === MedicalImagesType.LATERAL_SUPPORT_SURGERY
      );

      if (
        selectedSurgeryTool === POPUP_CONTENT_TYPES.GLOBAL_ALIGNMENT ||
        showSupportView
      ) {
        return supportView;
      } else {
        return view;
      }
    };

    const getComparedNeededView = () => {
      if (!comparedModalityViews || comparedModalityViews.length === 0) {
        return null;
      }
      const comparedView = comparedModalityViews[0];
      const comparedSupportView = comparedModalityViews.find(
        v => v.viewName === MedicalImagesType.LATERAL_SUPPORT_SURGERY
      );

      if (
        selectedSurgeryTool === POPUP_CONTENT_TYPES.GLOBAL_ALIGNMENT ||
        showSupportView
      ) {
        return comparedSupportView;
      } else {
        return comparedView;
      }
    };

    const currentView = getCurrentNeededView();
    const comparedView = getComparedNeededView();
    const areViewsAvailable =
      views && views.length > 0 && currentView && currentView.viewData;
    const areComparedViewsAvailable =
      isComparisonMode &&
      comparedModalityViews &&
      comparedModalityViews.length > 0 &&
      comparedView &&
      comparedView.viewData;

    return (
      <ViewLoader
        isLoading={isAlignLoading}
        message={remoteMedicalLoadingMessage}
      >
        {areViewsAvailable && (
          <div
            className={`Align-view-container ${
              isComparisonMode ? "comparison-mode" : ""
            }`}
          >
            {/* Current Plan View */}
            <div
              className={`Align-single-view ${
                isComparisonMode ? "comparison-view" : ""
              }`}
            >
              {isComparisonMode && (
                <div className="Align-view-label Align-view-label--current">
                  {`Current: ${currentPlanLabel}`}
                </div>
              )}
              <ImageViewer
                key={`image-container-${currentView.key}-${
                  selectedSurgeryTool || "default"
                }`}
                viewData={getCurrentNeededView().viewData}
                instance={view.instance}
                onContentLoad={() => {}}
                onCameraClick={() => {}}
                onBrightnessClick={() => {}}
                step={PLANNING_STATES.ALIGN}
                viewType={view.viewName}
                viewId={getCurrentNeededView().viewData.viewId}
                remoteRenderClient={renderClient}
                planId={currentPlanId}
                instanceId={view.instance?._id}
                displaySettingsViewType={getDisplaySettingsViewType(
                  view.viewName
                )}
                persistedDisplaySettings={
                  view.instance?.display_settings?.[
                    getDisplaySettingsViewType(view.viewName)
                  ]
                }
              >
                <div>
                  {!selectedSurgeryTool && !isComparisonMode && (
                    <div
                      style={{
                        position: "absolute",
                        top: 20,
                        left: 20,
                        zIndex: 5,
                      }}
                    >
                      <NeutralToggle
                        isOn={!showSupportView}
                        onToggle={() => setShowSupportView(!showSupportView)}
                        disabled={controlsDisabled}
                        noTitle={false}
                        titlePosition="left-right"
                        titleOff="PRE"
                        titleOn="PLAN"
                      />
                    </div>
                  )}
                  {isOverlayToolsActive && (
                    <div
                      style={{
                        position: "absolute",
                        top: 20,
                        right: 20,
                        zIndex: 5,
                      }}
                    >
                      <AlignSpineOverlayTools
                        toolsConfigs={dataBundle.toolConfigsFrontEnd}
                        currentPlanId={currentPlanId}
                        targetType={dataBundle.globalAlignmentAct?.[0]?.method}
                        disabled={controlsDisabled || !areViewsReady}
                        activeTools={overlayToolsState}
                        setActiveTools={setOverlayToolsState}
                      />
                    </div>
                  )}
                  <RemoteViewer
                    key={`remote-viewer-${currentView.key}-${
                      selectedSurgeryTool || "default"
                    }`}
                    remoteRenderClient={renderClient}
                    viewData={getCurrentNeededView().viewData}
                    sliderWidth="80%"
                    watermarkText="Use as a planning aid only - not for diagnostic or prediction of surgical results"
                    onAddMeasurement={
                      selectedSurgeryTool ===
                      POPUP_CONTENT_TYPES.GLOBAL_ALIGNMENT
                        ? () => {}
                        : () => {
                            measurementToolManager.executeOnView(
                              view.viewData,
                              addCustomMeasurement
                            );
                          }
                    }
                  />
                </div>
              </ImageViewer>
            </div>

            {/* Compared Plan View - Only shown in comparison mode */}
            {isComparisonMode && areComparedViewsAvailable && (
              <div className="Align-single-view comparison-view">
                <div className="Align-view-label Align-view-label--compared">
                  {`Compared: ${comparedPlanLabel}`}
                </div>
                <ImageViewer
                  key={`image-container-compared-${comparedView.key}`}
                  viewData={comparedView.viewData}
                  instance={comparedView.instance}
                  onContentLoad={() => {}}
                  onCameraClick={() => {}}
                  onBrightnessClick={() => {}}
                  step={PLANNING_STATES.ALIGN}
                  viewType={comparedView.viewName}
                  viewId={comparedView.viewData.viewId}
                  remoteRenderClient={renderClient}
                  planId={comparedPlanId}
                  instanceId={comparedView.instance?._id}
                  displaySettingsViewType={getDisplaySettingsViewType(
                    comparedView.viewName
                  )}
                  persistedDisplaySettings={
                    comparedView.instance?.display_settings?.[
                      getDisplaySettingsViewType(comparedView.viewName)
                    ]
                  }
                >
                  <div>
                    {isOverlayToolsActive && (
                      <div
                        style={{
                          position: "absolute",
                          top: 20,
                          right: 20,
                          zIndex: 5,
                        }}
                      >
                        <AlignSpineOverlayTools
                          toolsConfigs={dataBundle.toolConfigsFrontEnd}
                          currentPlanId={comparedPlanId}
                          targetType={
                            comparedGlobalAlignmentAct?.[0]?.method ||
                            dataBundle.globalAlignmentAct?.[0]?.method
                          }
                          disabled={controlsDisabled || !areComparedViewsReady}
                          activeTools={comparedOverlayToolsState}
                          setActiveTools={setComparedOverlayToolsState}
                        />
                      </div>
                    )}
                    <RemoteViewer
                      key={`remote-viewer-compared-${comparedView.key}`}
                      remoteRenderClient={renderClient}
                      viewData={comparedView.viewData}
                      sliderWidth="80%"
                      watermarkText="Use as a planning aid only - not for diagnostic or prediction of surgical results"
                      onAddMeasurement={() => {}}
                    />
                  </div>
                </ImageViewer>
              </div>
            )}

            {/* Loading indicator for compared views */}
            {isComparisonMode && !areComparedViewsAvailable && (
              <div className="Align-single-view comparison-view loading">
                <div className="Align-view-label Align-view-label--compared">
                  {`Compared: ${comparedPlanLabel}`}
                </div>
                <div className="comparison-loading-message">
                  Loading compared plan...
                </div>
              </div>
            )}

            {/* Rod Interaction Placeholder - Only show when rods tool is selected */}
            {selectedSurgeryTool === POPUP_CONTENT_TYPES.FUSION && (
              <div className="rod-interaction-area">
                <RodsSelector
                  rods={rods}
                  activeRodId={activeRodId}
                  hasL6={alignAnatomyProfile.hasL6}
                  hasT13={alignAnatomyProfile.hasT13}
                  onUpdateRod={handleUpdateRod}
                  onAddRod={handleAddRod}
                  onRemoveRod={handleRemoveRod}
                  screwSpecs={screwSpecs}
                  setScrewSpecs={updateScrewSpecs}
                  surgicalActs={surgicalActs}
                  onFirstVertebraClick={
                    vertebraRangeSelection.handleFirstVertebraChange
                  }
                  onSecondVertebraClick={
                    vertebraRangeSelection.handleSecondVertebraChange
                  }
                  availableVertebraItems={
                    vertebraRangeSelection.availableVertebraItems
                  }
                  getSelectableVertebrae={
                    vertebraRangeSelection.getSelectableVertebrae
                  }
                  getOccupiedVertebrae={
                    vertebraRangeSelection.getOccupiedVertebrae
                  }
                />
              </div>
            )}

            {/* Parameter Selector Viewer - Show support view when needed */}
            {showParameterSelectorViewer &&
              (selectedSurgeryTool === POPUP_CONTENT_TYPES.CAGES ||
                selectedSurgeryTool === POPUP_CONTENT_TYPES.OSTEOTOMY ||
                selectedSurgeryTool ===
                  POPUP_CONTENT_TYPES.SEGMENTAL_TRANSFORMATION) && (
                <div className="AlignPopupParameterSelector-viewer-container">
                  <div className="AlignPopupParameterSelector-viewer-content">
                    {(() => {
                      const supportView = currentModalityViews.find(
                        view =>
                          view.viewName ===
                          MedicalImagesType.LATERAL_SUPPORT_SURGERY
                      );

                      return (
                        supportView && (
                          <ImageViewer
                            viewData={supportView.viewData}
                            instance={supportView.instance}
                            onContentLoad={() => {}}
                            step={PLANNING_STATES.ALIGN}
                            viewType={supportView.viewName}
                            viewId={supportView.viewData.viewId}
                            remoteRenderClient={renderClient}
                            planId={currentPlanId}
                            instanceId={supportView.instance?._id}
                            displaySettingsViewType={getDisplaySettingsViewType(
                              supportView.viewName
                            )}
                            persistedDisplaySettings={
                              supportView.instance?.display_settings?.[
                                getDisplaySettingsViewType(supportView.viewName)
                              ]
                            }
                          >
                            {selectedSurgeryTool ===
                              POPUP_CONTENT_TYPES.OSTEOTOMY && (
                              <div className="AlignPopupParameterSelector-viewer-label">
                                Adjust the angle to simulate osteotomy
                              </div>
                            )}

                            <RemoteViewer
                              remoteRenderClient={renderClient}
                              viewData={supportView.viewData}
                              sliderWidth="80%"
                              onAddMeasurement={() => {}}
                            />
                          </ImageViewer>
                        )
                      );
                    })()}
                  </div>
                </div>
              )}

            {/* Tool Widget Panel */}
            {(() => {
              const widget = renderSurgeryWidget();
              return (
                widget && (
                  <div
                    className={`Align-tool-widget-panel${
                      selectedSurgeryTool ===
                      POPUP_CONTENT_TYPES.GLOBAL_ALIGNMENT
                        ? " global-alignment"
                        : ""
                    }`}
                  >
                    <div className="Align-tool-widget-header">
                      <div className="Align-tool-widget-title-area">
                        <div className="Align-tool-widget-title App-title-widgets ">
                          {widget.title}
                        </div>
                      </div>
                      <IconButton
                        icon={IconsEnum.CLOSE}
                        size="small"
                        isCircle={true}
                        noBgColor={true}
                        onClick={handleCloseToolWidget}
                        className="Align-tool-widget-close"
                        title={t("443")}
                      />
                    </div>
                    <div className="Align-tool-widget-content">
                      {widget.component}
                    </div>
                  </div>
                )
              );
            })()}
          </div>
        )}
      </ViewLoader>
    );
  };

  const renderTools = () => {
    const sppTools = () => {
      return (
        <div className="Visualization-region-tools-side">
          <ToolToggle
            text={t("409")}
            icon={IconsEnum.SPP}
            isSelected={activeSidebar === SurgerySidebarCategories.spp}
            onClick={handleSppToggle}
            shape={ToolShape.SQUARE}
            disabled={controlsDisabled}
          />
        </div>
      );
    };

    const historyTools = () => {
      return (
        <div className="Visualization-region-tools-side">
          <ToolToggle
            icon={IconsEnum.HISTORY}
            text={t("420")}
            isSelected={activeSidebar === SurgerySidebarCategories.history}
            onClick={handleHistoryToggle}
            shape={ToolShape.SQUARE}
            disabled={controlsDisabled}
          />
        </div>
      );
    };

    if (!loadingState.plan || !loadingState.surgicalActs) {
      return <div></div>;
    }

    const hasGlobalAlignment = !!(
      globalAlignmentAct || dataBundle.globalAlignmentAct?.length > 0
    );
    const isToolbarDisabled = controlsDisabled || isComparisonMode;

    return (
      <div
        className="Visualization-region-tools"
        style={
          controlsDisabled ? { pointerEvents: "none", opacity: 0.5 } : undefined
        }
      >
        {sppTools()}
        <ToolBar
          step={ROUTE_TYPES.ALIGN}
          onSurgeryToolSelect={handleSurgeryToolSelect}
          selectedSurgeryTool={selectedSurgeryTool}
          has3dInstances={has3dInstances}
          alignToolsState={{
            globalAlignment: false,
            osteotomy: !hasGlobalAlignment,
            segmentalTransformation: !hasGlobalAlignment,
            cages: !hasGlobalAlignment,
            fusion: !hasGlobalAlignment,
          }}
          disabled={isToolbarDisabled}
        />
        {historyTools()}
      </div>
    );
  };

  // Effects
  useEffect(() => {
    if (
      activeSidebar === SurgerySidebarCategories.spp &&
      dataBundle.annotations &&
      dataBundle.measurementConfigsFrontEnd
    ) {
      showOnlyRightSidebar(
        <AlignSpp
          measurementConfigs={dataBundle.measurementConfigsFrontEnd}
          customMeasurementConfigs={dataBundle.customMeasurementConfigsFrontEnd}
          onSppSelected={onSppSelected}
          onSppSelectedCustom={onSppSelectedCustom}
          activeParameters={activeParameters}
          removeCustomParameter={removeCustomParameter}
          instances={instances}
          refresh={validationRefresh}
          onTargetTypeChange={handleTargetTypeChange}
          selectedTargetType={
            dataBundle.globalAlignmentAct?.[0]?.method || TargetTypeEnum.GAP
          }
          originalMeasurements={originalMeasurements}
          currentMeasurements={currentMeasurements}
          targetMeasurements={targetMeasurements}
          compareMode={isComparisonMode}
          currentPlanLabel={currentPlanLabel}
          comparedPlanLabel={comparedPlanLabel}
          comparedCurrentMeasurements={comparedCurrentMeasurements}
          digitalTwinId={digitalTwinId}
          initialKeyParameters={initialKeyParameters}
          currentPlan={currentPlan}
          currentPlanId={currentPlanId}
          isDisabled={isComparedPlanLoading}
        />,
        SidebarSize.MEDIUM
      );
    } else if (activeSidebar === SurgerySidebarCategories.history) {
      showOnlyRightSidebar(
        <AlignToolsWidget
          onDeleteSurgicalAct={handleDeleteSurgicalAct}
          onDeleteGlobalAlignment={handleDeleteGlobalAlignment}
          onBulkDeleteSurgicalActs={handleBulkDeleteSurgicalActs}
          onEditSurgicalAct={handleEditSurgicalAct}
          onEditAllRods={handleEditAllRods}
          onEditGlobalAlignment={handleEditGlobalAlignment}
          currentSurgicalActs={surgicalActs}
          currentGlobalAlignment={globalAlignmentAct}
          comparedSurgicalActs={comparedSurgicalActs}
          comparedGlobalAlignment={comparedGlobalAlignment}
          compareMode={isComparisonMode}
          currentPlanLabel={currentPlanLabel}
          comparedPlanLabel={comparedPlanLabel}
          isPlanSelected={isCurrentPlanSelected}
          isSavingPlanSelection={isSavingSelection}
          onSelectPlan={handleSelectCurrentPlan}
          planName={currentPlanLabel}
          planSelectionDisabled={isComparedPlanLoading}
        />,
        SidebarSize.MEDIUM
      );
    } else {
      hideSidebars();
    }
  }, [
    activeSidebar,
    dataBundle.annotations,
    dataBundle.customMeasurementConfigsFrontEnd,
    dataBundle.measurementConfigsFrontEnd,
    showOnlyRightSidebar,
    hideSidebars,
    onSppSelected,
    onSppSelectedCustom,
    activeParameters,
    removeCustomParameter,
    instances,
    validationRefresh,
    handleDeleteSurgicalAct,
    surgicalActs,
    currentPlanId,
    globalAlignmentAct,
    handleDeleteGlobalAlignment,
    handleTargetTypeChange,
    selectedTargetType,
    handleBulkDeleteSurgicalActs,
    originalMeasurements,
    currentMeasurements,
    targetMeasurements,
    comparedCurrentMeasurements,
    handleEditSurgicalAct,
    handleEditGlobalAlignment,
    dataBundle.globalAlignmentAct,
    digitalTwinId,
    initialKeyParameters,
    currentPlan,
    comparedPlan,
    isComparisonMode,
    handleEditAllRods,
    isCurrentPlanSelected,
    isSavingSelection,
    handleSelectCurrentPlan,
    currentPlanLabel,
    comparedPlanLabel,
    comparedSurgicalActs,
    comparedGlobalAlignment,
    isComparedPlanLoading,
    areComparedViewsReady,
  ]);

  // Set first available modality as default (should be DX)
  useEffect(() => {
    if (groupedThumbnailsStandard?.length > 0 && !selectedModality) {
      const firstModality = groupedThumbnailsStandard[0].modality;
      setSelectedModality(firstModality);
    }
  }, [groupedThumbnailsStandard, selectedModality]);

  // Reset view toggle when a tool is selected
  useEffect(() => {
    if (selectedSurgeryTool) {
      setShowSupportView(false);
    }
  }, [selectedSurgeryTool]);

  return (
    <div className="Visualization-region-container">
      <div className="Visualization-region-header">
        <div className="Visualization-region-title App-title-widgets">
          {`Digital Twin: ${selectedTwinName}`}
        </div>
        {selectedTwin &&
          formatTwinCreationDate(selectedTwin, null, {
            locale: dateSettings?.locale,
            format: dateSettings?.format,
          }) && (
            <div className="Visualization-region-date">
              {formatTwinCreationDate(selectedTwin, null, {
                locale: dateSettings?.locale,
                format: dateSettings?.format,
              })}
            </div>
          )}
      </div>
      <div className="Visualization-region-content-no-tabs">
        <div className="Visualization-region-views">{renderAlignView()}</div>
        <div className="Visualization-region-tools-wrapper">
          {renderTools()}
        </div>
      </div>
      <div className="Visualization-region-plan-managment">
        <PlanTab persistedSelectedPlanId={persistedSelectedPlanId}></PlanTab>
      </div>
    </div>
  );
};

export default Align;
